import React, { useEffect, useState } from 'react';
import { Save, Plus, Trash2, CheckCircle2, XCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import FormControl from '../../common/FormControl';
import FileAnalyticsAPI from '../fileAnalyticsService';
import { AuthenticatorRecord, AuthPayloadRow } from '../types';

interface Props {
  isOpen: boolean;
  onClose(): void;
  onSaved(rec: AuthenticatorRecord): void;
  /** Pass to edit existing; omit to create. */
  existing?: AuthenticatorRecord | null;
}

function KeyValuePairRow({
  index,
  keyVal,
  value,
  onChangeKey,
  onChangeValue,
  onRemove,
  disabled,
}: {
  index: number;
  keyVal: string;
  value: string;
  onChangeKey(v: string): void;
  onChangeValue(v: string): void;
  onRemove(): void;
  disabled?: boolean;
}) {
  const [showPwd, setShowPwd] = useState(false);
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
      <div style={{ flex: 1 }}>
        <FormControl.Input
          label={index === 0 ? 'Key' : undefined}
          value={keyVal}
          onChange={e => onChangeKey(e.target.value)}
          placeholder="e.g. user_id"
          disabled={disabled}
        />
      </div>
      <div style={{ flex: 1, position: 'relative' }}>
        {index === 0 && (
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-heading)', marginBottom: 4 }}>
            Value
          </label>
        )}
        <div style={{ position: 'relative' }}>
          <input
            type={showPwd ? 'text' : 'password'}
            value={value}
            onChange={e => onChangeValue(e.target.value)}
            placeholder="value"
            disabled={disabled}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '8px 36px 8px 10px',
              border: '1px solid var(--border-default)', borderRadius: 6,
              fontSize: 13, color: 'var(--text-primary)',
              background: disabled ? 'var(--surface-muted)' : '#fff',
            }}
          />
          <button
            type="button"
            onClick={() => setShowPwd(v => !v)}
            disabled={disabled}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}
          >
            {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '8px 4px', flexShrink: 0 }}
        title="Remove field"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

const DEFAULT_ROWS: AuthPayloadRow[] = [
  { key: 'user_id', value: '' },
  { key: 'password', value: '' },
];

const AuthenticatorModal: React.FC<Props> = ({ isOpen, onClose, onSaved, existing }) => {
  const [name, setName] = useState('');
  const [loginUrl, setLoginUrl] = useState('');
  const [tokenPath, setTokenPath] = useState('access_token');
  const [rows, setRows] = useState<AuthPayloadRow[]>(DEFAULT_ROWS);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [tested, setTested] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setTestResult(null);
    setTested(existing?.session_valid ?? false);
    if (existing) {
      setName(existing.name);
      setLoginUrl(existing.login_url);
      setTokenPath(existing.token_path || 'access_token');
      // Pre-seed keys from stored payload_keys; values blank (write-only)
      const seedRows: AuthPayloadRow[] = (existing.login_payload_keys || []).map(k => ({ key: k, value: '' }));
      setRows(seedRows.length > 0 ? seedRows : DEFAULT_ROWS);
    } else {
      setName('');
      setLoginUrl('');
      setTokenPath('access_token');
      setRows(DEFAULT_ROWS.map(r => ({ ...r })));
    }
  }, [isOpen, existing?.id]);

  const updateRow = (i: number, field: 'key' | 'value', val: string) => {
    setTested(false); setTestResult(null);
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  };

  const removeRow = (i: number) => {
    setTested(false); setTestResult(null);
    setRows(prev => prev.filter((_, idx) => idx !== i));
  };

  const addRow = () =>
    setRows(prev => [...prev, { key: '', value: '' }]);

  const canSubmit = !!name.trim() && loginUrl.startsWith('https://') && rows.some(r => r.key.trim()) && tested;

  const handleSave = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        login_url: loginUrl.trim(),
        token_path: tokenPath.trim() || 'access_token',
        login_payload: rows.filter(r => r.key.trim()),
      };
      const saved = existing
        ? await FileAnalyticsAPI.updateAuthenticator(existing.id, payload)
        : await FileAnalyticsAPI.createAuthenticator(payload);
      onSaved(saved);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = existing
        ? await FileAnalyticsAPI.testAuthenticator(existing.id)
        : await FileAnalyticsAPI.testAuthenticatorInline({
            login_url: loginUrl.trim(),
            token_path: tokenPath.trim() || 'access_token',
            login_payload: rows.filter(r => r.key.trim()),
          });
      const ok = result.success;
      setTested(ok);
      setTestResult({
        success: ok,
        message: ok
          ? `Connected · token ${result.token_preview}`
          : result.error || 'Test failed',
      });
    } catch (err: any) {
      setTested(false);
      setTestResult({ success: false, message: err?.response?.data?.detail || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <Modal.Header>{existing ? 'Edit Authenticator' : 'New Authenticator'}</Modal.Header>
      <Modal.Body>
        <form className="flex flex-col gap-y-4">
          <FormControl.Input
            label="Name"
            required
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. ERP Login – Production"
            disabled={busy}
          />

          <fieldset className="field-group">
            <legend className="field-group-legend">Login Endpoint</legend>
            <div className="field-group-body">
              <FormControl.Input
                label="Login URL"
                required
                value={loginUrl}
                onChange={e => { setLoginUrl(e.target.value); setTested(false); setTestResult(null); }}
                placeholder="https://api.example.com/auth/login"
                disabled={busy}
              />
              <FormControl.Input
                label="Token Path"
                value={tokenPath}
                onChange={e => { setTokenPath(e.target.value); setTested(false); setTestResult(null); }}
                placeholder="access_token"
                disabled={busy}
              />
            </div>
          </fieldset>

          <fieldset className="field-group">
            <legend className="field-group-legend">Login Payload</legend>
            <div className="field-group-body">
              {rows.map((row, i) => (
                <KeyValuePairRow
                  key={i}
                  index={i}
                  keyVal={row.key}
                  value={row.value}
                  onChangeKey={v => updateRow(i, 'key', v)}
                  onChangeValue={v => updateRow(i, 'value', v)}
                  onRemove={() => removeRow(i)}
                  disabled={busy}
                />
              ))}
              <button
                type="button"
                onClick={addRow}
                disabled={busy}
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--brand)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0' }}
              >
                <Plus size={14} /> Add field
              </button>
            </div>
          </fieldset>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Button
              variant="outline"
              icon={testing ? Loader2 : undefined}
              onClick={handleTest}
              disabled={testing || busy || !loginUrl.startsWith('https://') || !rows.some(r => r.key.trim())}
              type="button"
            >
              {testing ? 'Testing…' : 'Register / Test Connection'}
            </Button>
            {testResult && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: testResult.success ? 'var(--green-700, #15803d)' : 'var(--red-600, #dc2626)' }}>
                {testResult.success
                  ? <CheckCircle2 size={14} />
                  : <XCircle size={14} />}
                {testResult.message}
              </span>
            )}
          </div>

          {error && <div className="modal-error-banner">{error}</div>}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="fill"
          icon={Save}
          onClick={handleSave}
          disabled={busy || !canSubmit}
          title={!tested ? 'Register / Test Connection must succeed before saving' : undefined}
          type="button"
        >
          {busy ? 'Saving…' : existing ? 'Save Changes' : 'Create Authenticator'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export { KeyValuePairRow };
export default AuthenticatorModal;
